<template>
 <v-card flat class="status-modal">
  <div class="status-header">
   <div class="status-icon" :class="`status-icon--${tone}`">
    <i class="fas" :class="icon"></i>
   </div>
   <div class="status-title">
    <slot name="title">
     <h3>{{ heading }}</h3>
    </slot>
    <span v-if="subtitleText" class="status-subtitle">{{ subtitleText }}</span>
   </div>
   <button type="button" class="close-btn" title="Close" @click="closeModalHandler"><i class="fas fa-times"></i></button>
  </div>

  <div class="status-body">
   <slot name="body">
    <label class="field-label">Remarks <span class="req">*</span></label>
    <v-textarea v-model="remarks" :placeholder="placeholder" outlined rows="3" no-resize hide-details :maxlength="maxRemarks"></v-textarea>
    <div class="char-count">{{ remarks.length }}/{{ maxRemarks }}</div>
   </slot>
  </div>

  <div class="status-footer">
   <button type="button" class="btn btn--cancel" @click="closeModalHandler">Cancel</button>
   <button type="button" class="btn" :class="`btn--${tone}`" @click="confirmActionHandler" :disabled="!remarks.trim() || submitting">{{ actionLabel }}</button>
  </div>
 </v-card>
</template>

<script>
import axios from "@/axios/axios-global";
import moment from "moment";
export default {
 name: "DeactivationModal",
 props: {
  title: {
   type: String,
   required: true,
   default: "",
  },
  token: String,
  type: Number,
  // optional line under the heading, e.g. "KA40B5685 · Maruti Tour S · Sedan"
  subtitle: { type: String, default: "" },
 },
 emits: ["close-modal"],
 data() {
  return {
   remarks: "",
   maxRemarks: 250,
   submitting: false,
   driverDetails: {},
   vehicleDetails: {},
   drawer: true,
  };
 },
 computed: {
  isReject() {
   return this.title === "Reject";
  },
  isActivate() {
   return this.title === "Activate";
  },
  entity() {
   return this.type === 1 ? "Driver" : "Vehicle";
  },
  tone() {
   return this.isActivate ? "green" : "red";
  },
  icon() {
   if (this.isActivate) return "fa-check";
   return this.isReject ? "fa-times" : "fa-ban";
  },
  heading() {
   return this.isReject ? "Reject" : `${this.title} ${this.entity}`;
  },
  actionLabel() {
   return this.title || "Confirm";
  },
  placeholder() {
   return this.isReject ? "Enter reason for rejection..." : `Enter reason for ${this.title.toLowerCase().replace("deactivate", "deactivation").replace("activate", "activation")}...`;
  },
  subtitleText() {
   if (this.subtitle) return this.subtitle;
   if (this.isReject) return "";
   if (this.type === 1) return [this.driverDetails.name, this.driverDetails.mobile].filter(Boolean).join(" · ");
   const v = this.vehicleDetails;
   const details = v.details || {};
   const model = [details.manufacturer && details.manufacturer.name, details.model && details.model.name].filter(Boolean).join(" ");
   return [v.vehicleno, model].filter(Boolean).join(" · ");
  },
 },
 mounted() {
  if (this.title !== "Reject") {
   if (this.type === 1) {
    this.getDriverDetails();
   } else {
    this.getVehicleDetails();
   }
  }
 },
 methods: {
  async getDriverDetails() {
   const payload = {
    driverId: this.token,
   };
   const response = await axios.post("driver/details", payload);
   this.driverDetails = response.data;
  },
  async getVehicleDetails() {
   const response = await axios.post("vehicle/details", { vehicleId: this.token });
   this.vehicleDetails = response.data;
  },
  closeModalHandler() {
   this.remarks = "";
   this.$emit("dialog-closed");
  },
  async confirmActionHandler() {
   if (this.title === "Reject") {
    this.$emit("callReject", this.remarks);
    return;
   }
   this.submitting = true;
   try {
    if (this.type === 1) {
     let url = "driver/deactivateDriver";
     if (this.driverDetails.trash) url = "driver/activate";
     const index = this.driverDetails.reason.length + 1 || 1;
     const payload = {
      driverId: this.driverDetails.token,
      id: index,
      date: moment.now(),
      reason: this.remarks,
      remarksBy: {
       name: localStorage.getItem("vendorName"),
      },
     };
     const response = await axios.post(url, payload);
     if (response.status === 200) this.$emit("change-status");
     this.$toasted.show(response.data, {
      position: "bottom-center",
      duration: 4000,
     });
    } else {
     let url = "vehicle/deactivate";
     if (this.vehicleDetails.trash) url = "vehicle/activate";
     const payload = {
      vehicleId: this.vehicleDetails.token,
      reason: this.remarks,
      user: localStorage.getItem("vendorName"),
     };
     const response = await axios.post(url, payload);
     if (response.status === 200) this.$emit("change-status");
     this.$toasted.show(response.data, {
      position: "bottom-center",
      duration: 4000,
     });
    }
   } catch (error) {
    console.error(error);
    this.$toasted.show("Something went wrong. Please try again.", {
     position: "bottom-center",
     duration: 4000,
    });
   } finally {
    this.submitting = false;
   }
  },
 },
};
</script>

<style scoped lang="scss">
$border: #e5e9f0;
$text: #1f2937;

.status-modal {
 padding: 0;
 border-radius: 10px;
}

.status-header {
 display: flex;
 align-items: center;
 gap: 18px;
 margin: 0 28px;
 padding: 22px 0 18px;
 border-bottom: 1px solid $border;
}

.status-icon {
 width: 64px;
 height: 64px;
 flex-shrink: 0;
 display: flex;
 align-items: center;
 justify-content: center;
 border-radius: 50%;
 font-size: 28px;
}
.status-icon--red {
 color: #e53935;
 background: #fde8e8;
}
.status-icon--green {
 color: #1e7b34;
 background: #e6f4ea;
}

.status-title {
 flex: 1;
 min-width: 0;

 h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: $text;
 }
}

.status-subtitle {
 display: block;
 margin-top: 2px;
 font-size: 15px;
 color: #6b7280;
}

.close-btn {
 align-self: flex-start;
 width: 34px;
 height: 34px;
 display: inline-flex;
 align-items: center;
 justify-content: center;
 font-size: 20px;
 color: #4b5563;
 border-radius: 6px;

 &:hover {
  background: #f3f4f6;
 }
}

.status-body {
 padding: 20px 28px 8px;
}

.field-label {
 display: block;
 margin-bottom: 6px;
 font-size: 15px;
 font-weight: 500;
 color: #374151;

 .req {
  color: #e53935;
 }
}

::v-deep .v-textarea fieldset {
 border-color: #cfd6e1;
 border-radius: 6px;
}

.char-count {
 margin-top: 6px;
 text-align: right;
 font-size: 13px;
 color: #6b7280;
}

.status-footer {
 display: flex;
 justify-content: flex-end;
 gap: 14px;
 padding: 16px 28px;
 border-top: 1px solid $border;

 .btn {
  height: 44px;
  padding: 0 26px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  border-radius: 6px;
  transition: filter 0.15s;

  &:hover:not(:disabled) {
   filter: brightness(0.95);
  }

  &:disabled {
   opacity: 0.55;
   cursor: not-allowed;
  }
 }

 .btn--cancel {
  color: $text;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
 }
 .btn--red {
  background: #e53935;
 }
 .btn--green {
  background: #1e9e3e;
 }
}
</style>
